/**
 * PicksConfigSection - The collapsible configuration area including league selector,
 * game/market/ban controls, and the generate button.
 */

import ErrorModal from '@components/ui/ErrorModal';
import UpgradeModal from '@components/ui/UpgradeModal';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { DEFAULT_PRESET_ID } from '@parlay-picker/shared/configPreset';
import {
  hasPlayerProps,
  isInSeason,
  type League,
  leagueToLeagueKey,
  SUPPORTED_LEAGUES,
} from '@parlay-picker/shared/league';
import type { LlmProvider } from '@parlay-picker/shared/llmProviders';
import {
  GAME_MARKET_DEFAULT_KEYS,
  getAvailableMarketsForLeague,
} from '@parlay-picker/shared/marketKey';
import type { ExternalApiHealth } from '@parlay-picker/shared/serviceHealth';
import type { PerModeQuotaUsage } from '@parlay-picker/shared/subscriptionTier';
import { todayEastern } from '@parlay-picker/shared/time';
import { useAnalysisConfigStore } from '@stores/analysisConfigStore';
import { usePresetsStore } from '@stores/presetsStore';
import { ChevronDown, FileText, RefreshCw, Sparkles } from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';
import { DEFAULT_ANALYSIS_CONFIG } from '../constants';
import type { Game } from '../utils';
import { groupGamesByDay } from '../utils';
import { GenerationHistoryDropdown } from './GenerationHistoryDropdown';
import { PicksControlsPanel } from './PicksControlsPanel';

// Provider types for admin dropdown
type ProviderSelection = LlmProvider | 'auto';

interface ErrorData {
  message: string;
  type: string;
  retryAfter?: number;
}

interface PicksConfigSectionProps {
  // State
  recs: GeneratedParlay[];
  isConfigCollapsed: boolean;
  onToggleCollapsed: () => void;
  /** Show the results after loading a generation. Distinct from the toggle: from the
   *  collapsed header a toggle re-opened the config the user had just dismissed. */
  onCollapseConfig: () => void;
  selectedLeague: League;
  onLeagueChange: (league: League) => void;
  leagueGames: Game[];
  loadingGames: boolean;
  refreshingGames: boolean;
  onRefreshGames: () => Promise<void>;
  selectedGames: string[];
  onToggleGame: (id: string) => void;
  onSelectAllGames: () => void;
  onDeselectAllGames: () => void;
  selectedMarkets: string[];
  onToggleMarket: (key: string) => void;
  bannedPlayers: string[];
  availablePlayers: { id?: string | number; name: string; teamAbbr?: string }[];
  loadingPlayers: boolean;
  onAddPlayer: (name: string) => void;
  onAddPlayers: (names: string[]) => void;
  onRemovePlayer: (name: string) => void;
  onRemovePlayers: (names: string[]) => void;
  showDetailedAnalysis: boolean;
  onShowDetailedAnalysisChange: (v: boolean) => void;
  isAdmin: boolean;
  selectedProvider: ProviderSelection;
  onProviderChange: (p: ProviderSelection) => void;
  apiHealth: ExternalApiHealth;
  onRetryHealth: () => Promise<void>;
  isGenerating: boolean;
  wasCached: boolean;
  onGenerate: (forceRefresh?: boolean) => Promise<void>;
  modeQuota: { used: number; limit: number; remaining: number } | null;
  analysisMode: AnalysisMode;
  onShowUpgrade: () => void;
  sportsbookName: string;
  // Error / upgrade modals
  error: ErrorData | null;
  onDismissError: () => void;
  showUpgradeModal: boolean;
  onDismissUpgrade: () => void;
  quotaUsage: PerModeQuotaUsage | null;
  updatePreferences: (prefs: Record<string, unknown>) => void;
  setSelectedGames: (ids: string[]) => void;
}

export function PicksConfigSection({
  recs,
  isConfigCollapsed,
  onToggleCollapsed,
  onCollapseConfig,
  selectedLeague,
  onLeagueChange,
  leagueGames,
  loadingGames,
  refreshingGames,
  onRefreshGames,
  selectedGames,
  onToggleGame,
  onSelectAllGames,
  onDeselectAllGames,
  selectedMarkets,
  onToggleMarket,
  bannedPlayers,
  availablePlayers,
  loadingPlayers,
  onAddPlayer,
  onAddPlayers,
  onRemovePlayer,
  onRemovePlayers,
  showDetailedAnalysis,
  onShowDetailedAnalysisChange,
  isAdmin,
  selectedProvider,
  onProviderChange,
  apiHealth,
  onRetryHealth,
  isGenerating,
  wasCached,
  onGenerate,
  modeQuota,
  analysisMode,
  onShowUpgrade,
  sportsbookName,
  error,
  onDismissError,
  showUpgradeModal,
  onDismissUpgrade,
  quotaUsage,
  updatePreferences,
  setSelectedGames,
}: PicksConfigSectionProps): React.ReactElement {
  const hasRecs = recs.length > 0;
  const { setConfig: setAnalysisConfig } = useAnalysisConfigStore();

  // Pre-compute league season status
  const todayStr = todayEastern();
  // biome-ignore lint/correctness/useExhaustiveDependencies: todayStr busts the memo daily
  const leagueChips = useMemo(
    () =>
      [...SUPPORTED_LEAGUES]
        .map((league) => ({
          league,
          inSeason: isInSeason(leagueToLeagueKey(league)),
        }))
        .sort((a, b) => {
          if (a.inSeason === b.inSeason) return 0;
          return a.inSeason ? -1 : 1;
        }),
    [todayStr],
  );

  const leagueMarkets = getAvailableMarketsForLeague(leagueToLeagueKey(selectedLeague));
  const groupedGames = groupGamesByDay(leagueGames);

  const handleLeagueClick = (league: League, inSeason: boolean) => {
    if (!inSeason || league === selectedLeague) return;
    onLeagueChange(league);
    setSelectedGames([]);
    const leagueKey = leagueToLeagueKey(league);
    updatePreferences({ preferredLeague: leagueKey });
    const { presets, activePresetId, switchPreset: switchToPreset } = usePresetsStore.getState();
    const activePreset = presets.find((p) => p.id === activePresetId);
    if (activePreset?.league && activePreset.league !== leagueKey) {
      switchToPreset(DEFAULT_PRESET_ID);
    }
    if (!hasPlayerProps(leagueKey)) {
      const hasAnyMarket = getAvailableMarketsForLeague(leagueKey).some(
        (m) =>
          useAnalysisConfigStore.getState().config.propTypes?.[m.key] ??
          DEFAULT_ANALYSIS_CONFIG.propTypes?.[m.key] ??
          false,
      );
      if (!hasAnyMarket) {
        setAnalysisConfig((prev) => {
          const newPropTypes = { ...prev.propTypes };
          for (const key of GAME_MARKET_DEFAULT_KEYS) {
            newPropTypes[key] = true;
          }
          return { ...prev, propTypes: newPropTypes };
        });
      }
    }
  };

  return (
    <div
      className={
        hasRecs
          ? isConfigCollapsed
            ? 'sticky top-16 z-20'
            : 'fixed inset-0 z-30 bg-theme-bg overflow-y-auto p-4'
          : ''
      }
      onClick={hasRecs && !isConfigCollapsed ? () => onToggleCollapsed() : undefined}
    >
      <div
        className={`bg-theme-surface rounded-lg ${hasRecs ? '' : 'p-3 sm:p-4'}`}
        onClick={hasRecs && !isConfigCollapsed ? (e) => e.stopPropagation() : undefined}
      >
        {/* Collapsible config summary header.
            role=button (not <button>) so the nested GenerationHistoryDropdown
            button + action icons are valid DOM (no button-in-button). */}
        {hasRecs && (
          <div
            role="button"
            tabIndex={0}
            onClick={onToggleCollapsed}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleCollapsed();
              }
            }}
            data-testid="picks-config-header"
            className="w-full flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 text-left hover:bg-theme-surface-hover/50 transition-colors cursor-pointer"
          >
            {/* Phones: tighter gaps fit the actions on this row from 375px; narrower, they can wrap below. */}
            <div className="flex flex-1 items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 shrink-0 rounded-lg bg-theme-primary/10 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-theme-primary"
                  aria-hidden="true"
                >
                  <line x1="4" x2="4" y1="21" y2="14" />
                  <line x1="4" x2="4" y1="10" y2="3" />
                  <line x1="12" x2="12" y1="21" y2="12" />
                  <line x1="12" x2="12" y1="8" y2="3" />
                  <line x1="20" x2="20" y1="21" y2="16" />
                  <line x1="20" x2="20" y1="12" y2="3" />
                  <line x1="2" x2="6" y1="14" y2="14" />
                  <line x1="10" x2="14" y1="12" y2="12" />
                  <line x1="18" x2="22" y1="16" y2="16" />
                </svg>
              </div>
              {/* Phones: the counts can move under the label, breaking only at " · "; from sm, inline as before. */}
              <div className="flex flex-wrap items-baseline gap-x-2 sm:block">
                <span className="text-sm font-medium text-theme-text">Configuration</span>
                <span className="text-xs text-theme-text-tertiary sm:ml-2">
                  {selectedGames.length}&nbsp;games · {selectedMarkets.length}&nbsp;markets
                  {bannedPlayers.length > 0 ? ` · ${bannedPlayers.length}\u00a0banned` : ''}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-1.5 ml-auto">
              {/* History dropdown — opens downward in collapsed bar */}
              <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <GenerationHistoryDropdown onLoadEntry={onCollapseConfig} direction="down" />
              </div>

              {/* Refresh games */}
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!refreshingGames) onRefreshGames();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    if (!refreshingGames) onRefreshGames();
                  }
                }}
                className="p-1.5 rounded-md hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
                title="Refresh games"
              >
                <RefreshCw className={`w-4 h-4 ${refreshingGames ? 'animate-spin' : ''}`} />
              </div>

              {/* Detailed analysis toggle */}
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onShowDetailedAnalysisChange(!showDetailedAnalysis);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onShowDetailedAnalysisChange(!showDetailedAnalysis);
                  }
                }}
                className={`p-1.5 rounded-md hover:bg-theme-surface-hover transition ${showDetailedAnalysis ? 'text-theme-primary' : 'text-theme-text-tertiary hover:text-theme-text'}`}
                title={showDetailedAnalysis ? 'Hide detailed analysis' : 'Show detailed analysis'}
              >
                <FileText className="w-4 h-4" />
              </div>

              {/* Quick regenerate */}
              <div
                role="button"
                tabIndex={isGenerating ? -1 : 0}
                aria-disabled={isGenerating}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isGenerating) onGenerate(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    if (!isGenerating) onGenerate(false);
                  }
                }}
                className={`p-1.5 rounded-md transition ${
                  isGenerating
                    ? 'text-theme-primary animate-pulse pointer-events-none opacity-50'
                    : 'text-theme-text-tertiary hover:text-theme-text hover:bg-theme-surface-hover'
                }`}
                title={isGenerating ? 'Generating...' : 'Regenerate picks'}
              >
                <Sparkles className="w-4 h-4" />
              </div>
              <ChevronDown
                className={`w-4 h-4 text-theme-text-tertiary transition-transform ${isConfigCollapsed ? '' : 'rotate-180'}`}
              />
            </div>
          </div>
        )}

        <div
          className={`${hasRecs ? 'p-3 sm:p-4 border-t border-theme-border/30' : ''} ${hasRecs && isConfigCollapsed ? 'hidden' : ''}`}
        >
          {/* Title — hidden when results are visible */}
          <div
            className={`flex flex-col @2xl:flex-row @2xl:items-center justify-between gap-2 mb-4 ${hasRecs ? 'hidden' : ''}`}
          >
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base sm:text-lg text-theme-text">
                  AI-Powered Parlay Recommendations
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    if (!refreshingGames) onRefreshGames();
                  }}
                  disabled={refreshingGames}
                  className="p-1.5 rounded-md hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition disabled:opacity-50"
                  title="Refresh games"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingGames ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <p className="text-theme-text-secondary text-xs sm:text-sm">
                Using real {sportsbookName} lines & player stats
              </p>
              <p className="text-theme-text-tertiary text-[10px] sm:text-xs mt-1">
                AI-generated analysis may contain errors. Not financial advice.
              </p>
            </div>
          </div>

          {/* League toggle — always visible */}
          <div className="mb-4">
            <p className="text-xs text-theme-text-tertiary mb-1.5">League</p>
            <div className="relative">
              <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
                {leagueChips.map(({ league, inSeason }) => (
                  <button
                    key={league}
                    type="button"
                    disabled={!inSeason}
                    onClick={() => handleLeagueClick(league, inSeason)}
                    title={!inSeason ? `${league} is off-season` : undefined}
                    className={`shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                      !inSeason
                        ? 'bg-theme-surface text-theme-text-tertiary opacity-40 cursor-not-allowed'
                        : selectedLeague === league
                          ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                          : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
                    }`}
                  >
                    {league}
                    {!inSeason && <span className="ml-1 text-[9px] opacity-70">(off-season)</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loadingGames ? (
            <div className="text-center py-6 text-theme-text-secondary">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-theme-primary border-t-transparent mb-2" />
              <p>Loading today's games...</p>
            </div>
          ) : leagueGames.length === 0 ? (
            <div className="text-center py-6 text-theme-text-secondary">
              <p>No {selectedLeague} games scheduled for today</p>
            </div>
          ) : (
            <PicksControlsPanel
              groupedGames={groupedGames}
              selectedGames={selectedGames}
              onToggleGame={onToggleGame}
              onSelectAllGames={onSelectAllGames}
              onDeselectAllGames={onDeselectAllGames}
              totalGames={leagueGames.length}
              selectedLeague={selectedLeague}
              markets={leagueMarkets}
              selectedMarkets={selectedMarkets}
              onToggleMarket={onToggleMarket}
              bannedPlayers={bannedPlayers}
              onAddPlayer={onAddPlayer}
              onAddPlayers={onAddPlayers}
              onRemovePlayer={onRemovePlayer}
              onRemovePlayers={onRemovePlayers}
              availablePlayers={availablePlayers}
              loadingPlayers={loadingPlayers}
              showDetailedAnalysis={showDetailedAnalysis}
              onShowDetailedAnalysisChange={onShowDetailedAnalysisChange}
              isAdmin={isAdmin}
              selectedProvider={selectedProvider}
              onProviderChange={onProviderChange}
              apiHealth={apiHealth}
              onRetryHealth={onRetryHealth}
              isGenerating={isGenerating}
              recs={recs}
              wasCached={wasCached}
              onGenerate={onGenerate}
              modeQuota={modeQuota}
              analysisMode={analysisMode}
              onShowUpgrade={onShowUpgrade}
              onLoadHistoryEntry={onCollapseConfig}
            />
          )}
        </div>
      </div>
      {/* Non-track errors — outside collapsible content so they're visible when config is collapsed */}
      {error && error.type !== 'track_error' && (
        <ErrorModal error={error} onDismiss={onDismissError} onRetry={() => onGenerate(false)} />
      )}

      {showUpgradeModal && quotaUsage && (
        <UpgradeModal
          onDismiss={onDismissUpgrade}
          mode={analysisMode}
          used={modeQuota?.used ?? 0}
          limit={modeQuota?.limit ?? 0}
          resetsAt={quotaUsage.resetsAt}
        />
      )}
    </div>
  );
}
